import { selectNodeAtPos } from '@atlaskit/editor-common/node-selection';
import type { EditorCommand, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { MalleableUiPlugin } from '../malleableUiPluginType';
import { getHeadingSection } from '../remix-heading-section';

export const BLOCK_NUDGE_SOURCE = 'blockNudge';

export type SelectRemixAnchorOptions = {
	/** Multi-block range; takes precedence over `nodePos`/`nodeType`. */
	from?: number;
	nodePos?: number;
	nodeType?: string;
	source?: string;
	to?: number;
};

export const applyHeadingSectionSelection = ({
	nodePos,
	source,
	tr,
}: {
	nodePos: number;
	source?: string;
	tr: Parameters<EditorCommand>[0]['tr'];
}): boolean => {
	if (source !== BLOCK_NUDGE_SOURCE) {
		return false;
	}

	const headingSection = getHeadingSection(tr.doc, nodePos);
	if (!headingSection) {
		return false;
	}

	const headingSectionSelection = TextSelection.create(
		tr.doc,
		headingSection.startPos,
		headingSection.endPos,
	);
	tr.setSelection(headingSectionSelection);
	return true;
};

/** Anchors remix selection — `from`/`to` for a multi-block span, else `nodePos`/`nodeType`. */
export const makeSelectRemixAnchor =
	(api: ExtractInjectionAPI<MalleableUiPlugin> | undefined) =>
	({ nodePos, nodeType, source, from, to }: SelectRemixAnchorOptions = {}): EditorCommand =>
	({ tr }) => {
		if (from !== undefined && to !== undefined) {
			tr.setSelection(TextSelection.create(tr.doc, from, to));
			api?.selection?.commands?.setBlockSelection(tr.selection)({ tr });
			return tr;
		}

		if (nodePos === undefined || !nodeType) {
			return tr;
		}

		if (fg('cc-maui-phase-3')) {
			const didApplyHeadingSelection = applyHeadingSectionSelection({ nodePos, source, tr });

			if (!didApplyHeadingSelection) {
				selectNodeAtPos(tr, nodePos, nodeType);
			}
		} else if (fg('platform_editor_maui_jira_updates')) {
			selectNodeAtPos(tr, nodePos, nodeType);
		} else {
			api?.blockControls?.commands.expandAndUpdateSelection({
				startPos: nodePos,
				nodeType,
				selection: tr.selection,
				isShiftPressed: false,
			})({ tr });
		}

		// If a multi-block TextSelection was set, signal to the selection plugin
		// so that ak-editor-selected-node decorations are applied to all selected nodes.
		if (tr.selection instanceof TextSelection) {
			api?.selection?.commands?.setBlockSelection(tr.selection)({ tr });
		}

		return tr;
	};
